import { inject, Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import { BehaviorSubject } from 'rxjs';

import { type Schema } from '../../../../amplify/data/resource';
import { ChecklistItem, ChecklistTemplate } from '../models/task.model';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class ChecklistService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) this._client = generateClient<Schema>();
    return this._client;
  }

  private userService = inject(UserService);

  private templatesSubject = new BehaviorSubject<ChecklistTemplate[]>([]);
  templates$ = this.templatesSubject.asObservable();

  /**
   * Last failure, for the UI to show. Swallowing these made a stale
   * `src/amplify_outputs.json` look like "nothing happens on click".
   */
  private errorSubject = new BehaviorSubject<string | null>(null);
  error$ = this.errorSubject.asObservable();

  clearError() {
    this.errorSubject.next(null);
  }

  /**
   * The generated model, or a named failure. `client.models.X` is undefined when
   * the bundled Amplify config predates the model's deployment — the config the
   * app imports (src/amplify_outputs.json) is not the one `ampx sandbox` writes
   * unless it is pointed there.
   */
  private model(name: 'ChecklistItem' | 'ChecklistTemplate'): any {
    const model = (this.client.models as any)[name];
    if (!model) {
      throw new Error(
        `${name} is missing from this build's Amplify config. ` +
        `src/amplify_outputs.json is out of date with the deployed backend.`
      );
    }
    return model;
  }

  private fail(action: string, e: unknown): null {
    const message = e instanceof Error ? e.message : String(e);
    console.error(`[Checklist] ${action} failed:`, e);
    this.errorSubject.next(`${action} failed — ${message}`);
    return null;
  }

  // ── Items on a task ───────────────────────────────────────────────────────

  /** A task's checklist, ordered. Not cached: it is only read when a task opens. */
  async listItems(taskId: string): Promise<ChecklistItem[]> {
    try {
      const result: any = await (this.model('ChecklistItem').listChecklistItemsByTaskId as any)(
        { taskId },
        { sortDirection: 'ASC', limit: 200 }
      );
      if (result.errors?.length) console.error('[Checklist] listItems errors', result.errors);
      return (result.data || []).map(this.mapItem);
    } catch (e) {
      this.fail('Loading the checklist', e);
      return [];
    }
  }

  async addItem(taskId: string, label: string, order: number): Promise<ChecklistItem | null> {
    try {
      const { data, errors } = await this.model('ChecklistItem').create({
        taskId, label, order, done: false,
      });
      if (errors?.length) throw new Error(errors[0].message);
      return data ? this.mapItem(data) : null;
    } catch (e) {
      return this.fail('Adding the item', e);
    }
  }

  /**
   * Tick or untick an item, stamping who did it — the same `User.id` convention as
   * `Task.validatedBy`, so the same lookup resolves the name.
   */
  async setDone(itemId: string, done: boolean): Promise<boolean> {
    try {
      const userId = this.userService.getCurrentUserSnapshot()?.id ?? null;
      const { errors } = await this.model('ChecklistItem').update({
        id: itemId,
        done,
        doneBy: done ? userId : null,
        doneAt: done ? new Date().toISOString() : null,
      });
      if (errors?.length) throw new Error(errors[0].message);
      return true;
    } catch (e) {
      this.fail('Ticking the item', e);
      return false;
    }
  }

  async renameItem(itemId: string, label: string): Promise<boolean> {
    try {
      const { errors } = await this.model('ChecklistItem').update({ id: itemId, label });
      if (errors?.length) throw new Error(errors[0].message);
      return true;
    } catch (e) {
      this.fail('Renaming the item', e);
      return false;
    }
  }

  /** Hard delete: a checklist line carries no history worth keeping. */
  async deleteItem(itemId: string): Promise<boolean> {
    try {
      const { errors } = await this.model('ChecklistItem').delete({ id: itemId });
      if (errors?.length) throw new Error(errors[0].message);
      return true;
    } catch (e) {
      this.fail('Deleting the item', e);
      return false;
    }
  }

  async reorderItems(items: ChecklistItem[]): Promise<void> {
    try {
      await Promise.all(items.map((item, index) =>
        this.model('ChecklistItem').update({ id: item.id!, order: index })
      ));
    } catch (e) {
      this.fail('Reordering the checklist', e);
    }
  }

  // ── Templates ─────────────────────────────────────────────────────────────

  async listTemplates(): Promise<ChecklistTemplate[]> {
    try {
      const templates: ChecklistTemplate[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.model('ChecklistTemplate').list as any)({
          filter: { isDeleted: { ne: true } },
          limit: 200,
          nextToken,
        });
        if (result.errors?.length) console.error('[Checklist] listTemplates errors', result.errors);
        templates.push(...(result.data || []).map(this.mapTemplate));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      templates.sort((a, b) => a.name.localeCompare(b.name));
      this.templatesSubject.next(templates);
      return templates;
    } catch (e) {
      this.fail('Loading templates', e);
      return [];
    }
  }

  async createTemplate(name: string, items: string[], tenantId?: string | null) {
    try {
      const { data, errors } = await this.model('ChecklistTemplate').create({
        name, items, tenantId: tenantId ?? null,
      });
      if (errors?.length) throw new Error(errors[0].message);
      await this.listTemplates();
      return data ? this.mapTemplate(data) : null;
    } catch (e) {
      this.fail('Saving the template', e);
      throw e;
    }
  }

  async updateTemplate(id: string, changes: { name?: string; items?: string[] }) {
    try {
      const { errors } = await this.model('ChecklistTemplate').update({ id, ...changes });
      if (errors?.length) throw new Error(errors[0].message);
      await this.listTemplates();
      return true;
    } catch (e) {
      this.fail('Updating the template', e);
      return false;
    }
  }

  /** Soft delete, so a template still referenced in someone's memory can be restored. */
  async deleteTemplate(id: string): Promise<boolean> {
    try {
      const { errors } = await this.model('ChecklistTemplate').update({
        id, isDeleted: true, deletedAt: new Date().toISOString(),
      });
      if (errors?.length) throw new Error(errors[0].message);
      await this.listTemplates();
      return true;
    } catch (e) {
      this.fail('Deleting the template', e);
      return false;
    }
  }

  /**
   * Append a template's items to a task, after whatever is already there, so
   * applying a second template does not wipe the first.
   */
  async applyTemplate(taskId: string, template: ChecklistTemplate, startOrder: number) {
    try {
      await Promise.all(template.items.map((label, i) =>
        this.model('ChecklistItem').create({
          taskId, label, order: startOrder + i, done: false,
        })
      ));
    } catch (e) {
      this.fail('Applying the template', e);
      throw e;
    }
  }

  private mapItem = (raw: any): ChecklistItem => ({
    id: raw.id,
    taskId: raw.taskId,
    label: raw.label,
    done: raw.done ?? false,
    order: raw.order ?? 0,
    doneBy: raw.doneBy ?? null,
    doneAt: raw.doneAt ?? null,
  });

  private mapTemplate = (raw: any): ChecklistTemplate => ({
    id: raw.id,
    name: raw.name,
    items: (raw.items ?? []).filter((i: any) => typeof i === 'string'),
    tenantId: raw.tenantId ?? null,
  });
}
